import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Receipt, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, imgUrl } from "@/lib/api";
import { inputCls, Field, Card, money, todayISO } from "./booksShared";

const EMPTY = { date: todayISO(), vendor: "", category: "Other", amount: "", deductible: true, notes: "", receipt: "" };

export const BooksExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const load = () =>
    api.get("/admin/books/expenses").then((r) => setExpenses(r.data)).catch(() => toast.error("Could not load expenses")).finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/admin/books/settings").then((r) => setCategories(r.data.expense_categories || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadReceipt = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/books/receipts", fd);
      setForm((f) => ({ ...f, receipt: data.path }));
      toast.success("Receipt attached");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.date || !form.amount) {
      toast.error("Date and amount are required.");
      return;
    }
    setBusy(true);
    const payload = { ...form, amount: parseFloat(form.amount) || 0 };
    try {
      if (editing) {
        await api.put(`/admin/books/expenses/${editing.id}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/admin/books/expenses", payload);
        toast.success("Expense saved");
      }
      setForm(EMPTY);
      setEditing(null);
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/books/expenses/${id}`);
      toast.success("Expense deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="books-expenses">
      <button
        onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(!showForm); }}
        className="flex items-center gap-2 bg-terra hover:bg-terra-hover text-parchment font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
        data-testid="books-add-expense-button"
      >
        <Plus className="w-4 h-4" /> Add Expense
      </button>

      {showForm && (
        <Card title={editing ? "Edit Expense" : "New Expense"} testid="books-expense-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Date *"><input type="date" value={form.date} onChange={set("date")} className={inputCls} data-testid="expense-date" /></Field>
            <Field label="Vendor"><input value={form.vendor} onChange={set("vendor")} className={inputCls} data-testid="expense-vendor" /></Field>
            <Field label="Category">
              <select value={form.category} onChange={set("category")} className={inputCls} data-testid="expense-category">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                {!categories.includes("Other") && <option value="Other">Other</option>}
              </select>
            </Field>
            <Field label="Amount ($) *"><input type="number" step="0.01" min="0" value={form.amount} onChange={set("amount")} className={inputCls} data-testid="expense-amount" /></Field>
            <Field label="Notes"><input value={form.notes} onChange={set("notes")} className={inputCls} data-testid="expense-notes" /></Field>
            <Field label="Receipt">
              <div className="flex items-center gap-3">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadReceipt(e.target.files?.[0])} data-testid="expense-receipt-file" />
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 border border-parchborder hover:border-dill text-ink/60 rounded-xl px-4 py-2.5 text-sm transition-colors"
                  data-testid="expense-receipt-upload"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
                  {form.receipt ? "Replace" : "Upload"}
                </button>
                {form.receipt && (
                  <a href={imgUrl(form.receipt)} target="_blank" rel="noopener noreferrer" data-testid="expense-receipt-preview">
                    <img src={imgUrl(form.receipt)} alt="Receipt" className="w-10 h-10 rounded-lg object-cover border border-parchborder" />
                  </a>
                )}
              </div>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink/70">
            <input type="checkbox" checked={form.deductible} onChange={(e) => setForm((f) => ({ ...f, deductible: e.target.checked }))} className="accent-dill" data-testid="expense-deductible" />
            Tax deductible
          </label>
          <div className="flex gap-3">
            <button onClick={save} disabled={busy} className="bg-dill hover:bg-terra disabled:opacity-50 text-parchment font-bold rounded-full px-6 py-2.5 text-sm transition-colors flex items-center gap-2" data-testid="expense-save-button">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Save Changes" : "Save Expense"}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-sm text-ink/50 hover:text-terra transition-colors" data-testid="expense-cancel-button">Cancel</button>
          </div>
        </Card>
      )}

      <Card title={`Expenses (${expenses.length})`} testid="books-expenses-table">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-dill" /></div>
        ) : expenses.length === 0 ? (
          <p className="text-sm text-ink/50 italic">No expenses yet — add your first one above.</p>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-widest text-ink/45 text-left border-b border-parchborder">
                  <th className="py-2 pr-4">Date</th><th className="pr-4">Vendor</th><th className="pr-4">Category</th>
                  <th className="pr-4 text-right">Amount</th><th className="pr-4">Deductible</th><th className="pr-4">Receipt</th><th className="pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id} className="border-b border-parchborder/60" data-testid={`expense-row-${e.id}`}>
                    <td className="py-2.5 pr-4 font-mono text-xs whitespace-nowrap">{e.date}</td>
                    <td className="pr-4">{e.vendor || "—"}</td>
                    <td className="pr-4 text-ink/60">{e.category}</td>
                    <td className="pr-4 text-right font-semibold">{money(e.amount)}</td>
                    <td className="pr-4 text-ink/60">{e.deductible ? "Yes" : "No"}</td>
                    <td className="pr-4">
                      {e.receipt ? (
                        <a href={imgUrl(e.receipt)} target="_blank" rel="noopener noreferrer" className="text-terra hover:underline inline-flex items-center gap-1 text-xs" data-testid={`expense-receipt-view-${e.id}`}>
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : "—"}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => { setEditing(e); setForm({ ...EMPTY, ...e }); setShowForm(true); }} className="p-1.5 text-ink/40 hover:text-dill transition-colors" aria-label="Edit expense" data-testid={`expense-edit-${e.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(e.id)} className="p-1.5 text-ink/40 hover:text-terra transition-colors" aria-label="Delete expense" data-testid={`expense-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
